import LocalStorageService from '@/services/localStorageService'
import PermissionService from '@/services/permissionService'

const storageKeys = {
  token: 'token',
  selectedBillingCompany: 'selectedBillingCompany',
  selectedFacility: 'selectedFacility',
  user: 'user',
  billingCompanies: 'billingCompanies',
  userPermissions: 'userPermissions',
  rememberme: 'rememberme'
}

class SessionService {
  token
  selectedBillingCompany
  selectedFacility
  user
  staff
  billingCompanies
  keepUserLoggedIn
  promptedPermissions
  userPermissions
  rememberme

  init () {
    try {
      this.token = this.getPersistedItem(storageKeys.token)
      this.selectedBillingCompany = JSON.parse(this.getPersistedItem(storageKeys.selectedBillingCompany))
      this.selectedFacility = JSON.parse(this.getPersistedItem(storageKeys.selectedFacility))
      this.user = JSON.parse(this.getPersistedItem(storageKeys.user))
      this.billingCompanies = JSON.parse(this.getPersistedItem(storageKeys.billingCompanies))
      this.userPermissions = JSON.parse(this.getPersistedItem(storageKeys.userPermissions))
      this.rememberme = JSON.parse(this.getPersistedItem(storageKeys.rememberme))
    } catch (err) {
      throw new Error(err)
    }
  }

  isUserLoggedIn () {
    return this.hasOwnProperty('user') && this.user !== null && this.hasOwnProperty('billingCompanies') && this.billingCompanies !== null && this.hasOwnProperty('token') && this.token !== null
  }

  isUserLoggedInWithBillingCompany () {
    return this.isUserLoggedIn() && this.hasOwnProperty('selectedBillingCompany') && this.selectedBillingCompany !== null && this.hasPermissions()
  }

  isUserLoggedInWithSelectedFacility () {
    return this.isUserLoggedInWithBillingCompany() && this.hasOwnProperty('selectedFacility') && this.selectedFacility !== null
  }

  hasUserBeenPromptedForPermissions () {
    return !!this.promptedPermissions
  }

  hasPermissions () {
    return this.hasOwnProperty('userPermissions') && (!!this.userPermissions)
  }

  hasAdminRoleType () {
    return this.hasPermissions() && (!!this.userPermissions.adminRoleType)
  }

  permissions = {
    canManageOperators (userPermissions) {
      return PermissionService.canManageOperators(userPermissions.adminRoleType)
    },

    canEditContent (userPermissions) {
      return PermissionService.canEditContent(userPermissions.adminRoleType)
    },

    isHQAdministrator (userPermissions) {
      return PermissionService.isHQAdministrator(userPermissions.adminRoleType)
    },

    isCompanySuperAdmin (userPermissions) {
      return PermissionService.isCompanySuperAdmin(userPermissions.adminRoleType)
    },

    isCompanyAdmin (userPermissions) {
      return PermissionService.isCompanySuperAdmin(userPermissions.adminRoleType)
    },

    isSuperAdmin (userPermissions, user) {
      return PermissionService.isSuperAdmin(userPermissions.adminRoleType, user.accountUsername)
    },

    isDeveloperOperator (userPermissions) {
      return PermissionService.isDeveloperOperator(userPermissions)
    },

    isTesterOperator (userPermissions) {
      return PermissionService.isTesterOperator(userPermissions)
    },

    isSupportOperator (userPermissions) {
      return PermissionService.isSupportOperator(userPermissions)
    }
  }

  hasPermission (value) {
    if (this.permissions.hasOwnProperty(value)) {
      return this.hasAdminRoleType() && this.permissions[value](this.userPermissions, this.user)
    }
    return true
  }

  setToken (token) {
    this.token = token

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.token, token)
    }
  }

  clearToken () {
    this.token = null

    if (this.keepUserLoggedIn) {
      this.deletePersistedItem(storageKeys.token)
    }
  }

  setUser (user) {
    this.user = user

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.user, JSON.stringify(user))
    }
  }

  setRememberme () {
    this.rememberme = true

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.rememberme, JSON.stringify(true))
    }
  }

  setBillingCompanies (billingCompanies) {
    this.billingCompanies = billingCompanies

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.billingCompanies, JSON.stringify(billingCompanies))
    }
  }

  setselectedBillingCompany (selectedBillingCompany) {
    this.selectedBillingCompany = selectedBillingCompany

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.selectedBillingCompany, JSON.stringify(selectedBillingCompany))
    }
  }

  setselectedFacility (selectedFacility) {
    this.selectedFacility = selectedFacility

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.selectedFacility, JSON.stringify(selectedFacility))
    }
  }

  setselectedChain (selectedChains) {
    this.selectedFacility = selectedChains

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.selectedFacility, JSON.stringify(selectedChains))
    }
  }

  setUserPermissions (userPermissions) {
    this.userPermissions = userPermissions

    if (this.keepUserLoggedIn) {
      this.persistItem(storageKeys.userPermissions, JSON.stringify(userPermissions))
    }
  }

  clear () {
    this.token = null
    this.selectedBillingCompany = null
    this.selectedFacility = null
    this.user = null
    this.staff = null
    this.billingCompanies = null
    this.userPermissions = null
    this.rememberme = null

    this.deleteAllPersistedItems()
  }

  persistItem (key, value) {
    return LocalStorageService.set(key, value)
  }

  getPersistedItem (key) {
    return LocalStorageService.get(key)
  }

  deletePersistedItem (key) {
    return LocalStorageService.remove(key)
  }

  deleteAllPersistedItems () {
    return LocalStorageService.clear()
  }
}

export default SessionService
